/**
 * API calls for the admin Report Quality tab.
 *
 * Auth is the app's cookie session, so every request carries `credentials: 'include'` —
 * there is no bearer token anywhere in this codebase.
 *
 * Every endpoint here is admin-only on the backend, deliberately: the classifier's own model card
 * says its score must not be shown to customers.
 */

const API_URL = import.meta.env.VITE_API_URL || '';

const BASE = '/admin/report-quality';

/** Verdict buckets, worst to best. */
export type Bucket = 'low' | 'mid' | 'high';

/** `ok` means scored; `error` means the run failed and `error` says why. */
export type ClassificationStatus = 'ok' | 'error';

export interface ClassificationSummary {
  status: ClassificationStatus;
  bucket: Bucket | null;
  score: number | null;
  confidence: number | null;
  /** P(low) — the classifier's intended ranking key for a review queue. */
  review_priority: number | null;
  classified_at: string | null;
  error: string | null;
}

/** A scored artifact: a simulation report or an hcv2 chat session. */
export type ItemKind = 'simulation' | 'chat';

export interface SimulationRow {
  /** `ratable_items`' namespace: "42" for a simulation, "chat_42" for a chat session. */
  item_ref: string;
  kind: ItemKind;
  id: number;
  name: string | null;
  use_case: string | null;
  created_at: string | null;
  is_ratable: boolean | null;
  /** Null means never classified — a different state from a failed run, which has status 'error'. */
  classification: ClassificationSummary | null;
  /** Answered by the backend, never inferred from use_case. Null on the list response. */
  reground_eligible?: boolean | null;
}

export interface ClassificationDetail extends ClassificationSummary {
  id: number;
  use_case: string | null;
  reasoning: string | null;
  observations: { supporting?: string[]; detracting?: string[] } | null;
  anchors: Anchor[] | null;
  detail: Record<string, unknown> | null;
  warning: string | null;
  used_llm_judge: boolean | null;
  llm_judge_reasoning: string | null;
  model_version: string | null;
  /** What the classifier was actually shown, so an odd verdict can be traced back to its input. */
  raw_chars: number | null;
  sent_chars: number | null;
  personas_omitted: number | null;
  images_omitted: number | null;
  /** Null is "never asked"; a stored `{status:'skipped'}` is "asked and declined". */
  reground?: RegroundPayload | null;
}

/** A human-rated report the model found nearest to this one — the most useful part for a reviewer. */
export interface Anchor {
  rating: number;
  bucket: Bucket;
  similarity: number;
  rater_reasoning: string | null;
}

export type RadiusLevel = '0' | '1' | '2' | '3';

/** One sentence of the report, and what it traces back to. */
export interface RadiusSentence {
  text: string;
  radius?: number | null;
  /** Present for every sentence in v2; in v1 only on the top rung, and off-vocabulary. */
  tag?: string | null;
  /** The consumer statement behind it. Null means it traces to nothing. */
  backing?: string | null;
}

/** One prose block's sentences, keyed in `items` by the block's path. */
export interface RadiusItem {
  radius?: number | null;
  tag?: string | null;
  sentences?: RadiusSentence[] | null;
}

export interface RegroundRadius {
  /** Absent on payloads written before 2026-08-26; 2 renamed the rungs AND changed what 1 and 2
   *  mean, so a stored payload must be read with its OWN scale, never the newest one. */
  scale_version?: number | null;
  /** The service's own rung names — rendered, never hardcoded. */
  scale: Record<string, string>;
  /** v2's canonical rung order. Absent in v1, where RADIUS_ORDER stands in. */
  tags?: string[] | null;
  distribution?: Record<string, number> | null;
  /** v2 keys the same counts by tag name; both are sent, so either can drive the bar. */
  distribution_by_tag?: Record<string, number> | null;
  sentences_scored: number | null;
  novel_count: number | null;
  /** v2's name for novel_count; the service sends both. */
  directional_count?: number | null;
  corpus_consumers: number | null;
  corpus_size: number | null;
  /** Per-block sentence attribution. Present in v1 and v2, but absent when the report carried no
   *  annotate output for the service to work from. */
  items?: Record<string, RadiusItem> | null;
}

export interface RegroundDecision {
  path: string;
  kept?: boolean | null;
  reason?: string | null;
  before?: string | null;
  after?: string | null;
  actions?: Array<{ original?: string; action?: string; why?: string }> | null;
}

export interface RegroundAudit {
  rating_before: { expected_rating_1to5: number | null } | null;
  rating_after: { expected_rating_1to5: number | null } | null;
  rating_gain: number | null;
  /** The service judged its own rewrite no better and rolled it back. Expected, not a failure. */
  reverted: boolean | null;
  blocks_flagged: number | null;
  blocks_changed: number | null;
  sentences_flagged: number | null;
  untraceable_before: number | null;
  untraceable_after: number | null;
  rater_complaints_not_fixable_by_prose?: string[] | null;
  decisions?: RegroundDecision[] | null;
}

export interface RegroundPayload {
  status: 'ok' | 'skipped' | 'error';
  reason?: string | null;
  error?: string | null;
  reground?: RegroundAudit | null;
  radius?: RegroundRadius | null;
}

export interface SimulationListResponse {
  simulations: SimulationRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface SimulationDetailResponse {
  simulation: SimulationRow;
  classifications: ClassificationDetail[];
}

export interface UseCaseOptions {
  use_cases: string[];
  /** Use cases the classifier reports itself as unreliable on. Named by the backend so there is
   *  one home for that advice rather than a copy in the UI. */
  unreliable: string[];
}

export type SortMode = 'recent' | 'priority';

/** The bucket filter's "never classified" option — matches the backend's pseudo-bucket. */
export const BUCKET_UNCLASSIFIED = 'unclassified';

export interface ListParams {
  search?: string;
  useCase?: string;
  bucket?: string;
  kind?: ItemKind;
  sort?: SortMode;
  limit?: number;
  offset?: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });

  if (response.status === 403) {
    throw new Error('Report quality is admin-only.');
  }
  if (!response.ok) {
    // Surface the backend's own message where it has one — it explains a failed re-classify
    // (broker down) far better than a status code does.
    let detail = '';
    try {
      detail = (await response.json())?.detail ?? '';
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail || `Request failed (${response.status})`);
  }
  return response.json();
}

export const fetchSimulations = (params: ListParams = {}) => {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.useCase) qs.set('use_case', params.useCase);
  if (params.bucket) qs.set('bucket', params.bucket);
  if (params.kind) qs.set('kind', params.kind);
  if (params.sort) qs.set('sort', params.sort);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const suffix = qs.toString() ? `?${qs}` : '';
  return request<SimulationListResponse>(`${BASE}/simulations${suffix}`);
};

export const fetchClassifications = (itemRef: string) =>
  request<SimulationDetailResponse>(`${BASE}/simulations/${encodeURIComponent(itemRef)}`);

export const requestReclassify = (itemRef: string) =>
  request<{ queued: boolean; item_ref: string; task_id: string | null }>(
    `${BASE}/simulations/${encodeURIComponent(itemRef)}/classify`,
    { method: 'POST' },
  );

/** Queue a grounding audit. 409 when there is nothing to audit; the backend's sentence carries. */
export const requestReground = (itemRef: string) =>
  request<{ queued: boolean; item_ref: string; task_id: string | null }>(
    `${BASE}/simulations/${encodeURIComponent(itemRef)}/reground`,
    { method: 'POST' },
  );

/**
 * A chat session's transcript, for the detail pane. Reuses the admin-guarded endpoint that
 * already serves this (misleadingly filed under free-tier, but not free-tier scoped) rather than
 * adding a second way to read the same rows.
 */
export const fetchChatMessages = (sessionId: number) =>
  request<{ id: number; title: string | null; messages: ChatMessage[] }>(
    `/admin/free-tier/chats/${sessionId}`,
  );

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  metadata?: { visual?: unknown } | null;
}

export const fetchUseCaseOptions = () => request<UseCaseOptions>(`${BASE}/use-cases`);
